import React from 'react';
import { cn } from '@utils/cn';
import { InvoiceStatusBadge } from '@components/atoms/InvoiceStatusBadge';
import { Invoice } from '@models/invoice.model';

interface DraftsListProps {
  drafts: Invoice[];
  onOpenDraft: (invoiceId: string) => void;
  className?: string;
}

const formatCurrency = (value: number): string => {
  return `$ ${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const relativeTime = (iso?: string): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const DraftsList: React.FC<DraftsListProps> = ({
  drafts,
  onOpenDraft,
  className = '',
}) => {
  if (drafts.length === 0) {
    return (
      <div className={cn('flex-1 flex flex-col items-center justify-center px-6 py-10', className)}>
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        </div>
        <p className="text-base font-medium text-gray-900">No invoice drafts yet</p>
        <p className="text-sm text-gray-500 mt-1 max-w-sm text-center">
          Start a draft from the recipient row above. We'll pre-select charge-to-client items so you
          can adjust from there.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('flex-1 overflow-y-auto px-6 py-4', className)}>
      <p className="text-sm text-gray-600 mb-3">Resume a draft</p>

      <ul className="space-y-2">
        {drafts.map((draft) => {
          const itemCount = draft.lineItems?.length ?? 0;
          const linkedCount = draft.lineItems?.filter((item) => item.sourceEntityId).length ?? 0;
          const manualCount = itemCount - linkedCount;
          return (
            <li
              key={draft.invoiceId}
              tabIndex={0}
              role="button"
              onClick={() => onOpenDraft(draft.invoiceId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onOpenDraft(draft.invoiceId);
              }}
              className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50/30 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <InvoiceStatusBadge status={draft.status} size="sm" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {draft.orderDescription || `Draft ${draft.invoiceId.slice(0, 8)}`}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {itemCount} item{itemCount === 1 ? '' : 's'}
                      {linkedCount > 0 && ` · ${linkedCount} linked`}
                      {manualCount > 0 && ` · ${manualCount} manual`}
                      {' · '}
                      To <span className="font-mono">{draft.recipientEmail}</span>
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(Number(draft.total))}</p>
                  <p className="text-xs text-gray-400 mt-0.5 flex items-center justify-end gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Saved {relativeTime(draft.updatedAt)}
                  </p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
